import { inject, Injectable } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  map,
  Observable,
  tap,
} from 'rxjs';
import { ScreenService } from 'src/app/core/services/screen';

import { MatDialog } from '@angular/material/dialog';
import { Params, Router } from '@angular/router';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faArrowRightFromBracket,
  faBook,
  faChartLine,
  faClipboardList,
  faComments,
  faFileInvoiceDollar,
  faHandshake,
  faHeadset,
  faListCheck,
  faMoneyCheckDollar,
  faRetweet,
  faUser,
  faUserPen,
  faUsersCog,
} from '@fortawesome/free-solid-svg-icons';
import { ModalConferenciaImportacaoComponent } from 'src/app/features/onboarding/components/modal-conferencia-importacao';
import { environment } from 'src/environments/environment';
import {
  CHAT_IA_DISPONIVEL,
  URL_DO_ASSISTENTE_IZII,
} from '../../config/chat-ia';
import { OnboardingStatus } from '../../enum/OnboardingStatus';
import { TipoUsuario } from '../../enum/TipoUsuario';
import { Usuario } from '../../models/auth/Usuario';
import { downloadFileByUrl } from '../../utils/downloadFileByUrl';
import { AuthService } from '../auth';
import { LoaderService } from '../loader';
import { LabelService } from '../label/label.service';

export interface MenuChild {
  faIcon?: IconProp;
  label?: string;
  url?: string;
  queryParams?: Params;
  onClick?: () => void;
  children?: MenuChild[];
}

export interface MenuItem {
  faIcon: IconProp;
  label: string;
  url?: string;
  queryParams?: Params;
  onClick?: () => void;

  children?: MenuChild[];
}

@Injectable({
  providedIn: 'root',
})
export class SharedService {
  router = inject(Router);
  loader = inject(LoaderService);
  authService = inject(AuthService);
  screenService = inject(ScreenService);
  matDialog = inject(MatDialog);
  labelService = inject(LabelService);
  private readonly chatIaDisponivel = inject(CHAT_IA_DISPONIVEL);

  usuario!: Usuario;
  onboardingStatus!: OnboardingStatus;
  temPermissao = this.temPermissaoFactory();

  menuItems$: Observable<MenuItem[]> = combineLatest([
    this.authService.usuario$.pipe(filter(Boolean)),
    this.authService.onboardingStatus$,
  ]).pipe(
    tap(([user, onboardingStatus]) => {
      this.usuario = user;
      this.onboardingStatus = onboardingStatus ?? OnboardingStatus.NaoIniciado;
    }),
    map(() => {
      const menuItems: (MenuItem | null)[] = [];

      // 1- Dashboard
      menuItems.push(this.getMenuDashboard());

      // 1.1- Assistente Izii (so com a chave chatIaDisponivel: dev e local)
      menuItems.push(this.getMenuAssistenteIzii());

      // 2- Área Administrativa
      if (this.shouldShowAdminAreaMenu()) {
        menuItems.push(this.getMenuAreaAdministrativa());
      }

      // 2.1- Menu Corretoras
      menuItems.push(this.getMenuCorretoras());

      // 3- Painel de Atendimento
      menuItems.push(this.getMenuAtendimento());

      // 4- Painel de Tarefas
      menuItems.push(this.getMenuTarefas());

      // 5- Cadastro
      menuItems.push(this.getMenuCadastro());

      // 6- Área do Beneficiário
      if (this.shouldShowAreaBeneficiarioMenu()) {
        menuItems.push(this.getMenuAreaBeneficiario());
      }

      // 7- Faturas
      menuItems.push(this.getMenuBatimentoDeFatura());

      // 8- Baixar Manual do Hub
      menuItems.push(this.getMenuManual());

      // 9- Sair
      menuItems.push(this.getMenuSair());

      return menuItems.filter(Boolean) as MenuItem[];
    })
  );

  private isSidenavOpenedSubject$ = new BehaviorSubject(
    window.innerWidth >= 900
  );

  constructor() {
    this.registerCloseSidenavOnSmallWidth();
  }

  get isSidenavOpened$() {
    return this.isSidenavOpenedSubject$.asObservable();
  }

  toggleSidenav(): void {
    const currentValue = this.isSidenavOpenedSubject$.getValue();

    this.isSidenavOpenedSubject$.next(!currentValue);
  }

  private registerCloseSidenavOnSmallWidth(): void {
    this.screenService.screenWidth$.subscribe((screenWidth) => {
      this.isSidenavOpenedSubject$.next(screenWidth >= 1190);
    });
  }

  private getMenuDashboard(): MenuItem {
    return {
      label: 'Dashboard',
      faIcon: faChartLine,
      url: '/dashboard',
    };
  }

  // Chat com IA sobre a empresa selecionada (ultimas faturas, robos, cadastro,
  // leitor de e-mail), no primeiro nivel logo abaixo do Dashboard. So com a
  // chave chatIaDisponivel ligada (dev e local): em hml e producao o item nao
  // existe e a rota tambem nao casa (chatIaDisponivelGuard).
  private getMenuAssistenteIzii(): MenuItem | null {
    if (!this.chatIaDisponivel) {
      return null;
    }

    return {
      label: 'Assistente Izii',
      faIcon: faComments,
      url: URL_DO_ASSISTENTE_IZII,
    };
  }

  private getMenuPedidoDeCarga(): MenuItem {
    return {
      label: 'Pedido de Carga',
      faIcon: faMoneyCheckDollar,
      url: '/pedido-de-carga',
    };
  }

  private getMenuIntegracoes(): MenuItem {
    return {
      label: this.labelService.get('integracoesComProvedores'),
      faIcon: faRetweet,
      children: [
        // {
        //   label: 'Integrações Manuais',
        //   url: '/mov-cad-manual',
        // },
        {
          label: 'Integrações',
          url: '/integracoes',
        },
        {
          label: 'Logs de integração',
          url: '/integracoes/logs',
        },
        {
          label: 'Conferencia de Importação',
          onClick: () => this.abrirModalConferenciaImportacao(),
        },
      ],
    };
  }

  private getMenuCorretoras(): MenuItem {
    return {
      label: 'Corretoras',
      faIcon: faHandshake,
      children: [
        {
          label: 'Gestão de Portais',
          url: '/gestao-dos-portais',
        },
        {
          label: 'Minhas Escolhas',
          url: '/escolhas',
        },
      ],
    };
  }

  private getMenuCadastro(): MenuItem {
    const shouldAddMenuProvedores =
      this.onboardingStatus >= OnboardingStatus.Empresa &&
      this.temPermissao('Provedores');

    const shouldAddMenuEmpresa =
      this.onboardingStatus >= OnboardingStatus.Contrato &&
      this.temPermissao('Estrutura Da Empresa');

    const shouldAddMenuContratos =
      this.onboardingStatus >= OnboardingStatus.Beneficiarios &&
      this.temPermissao('Contratos');

    let children: MenuChild[];

    if (environment.production) {
      children = [
        {
          label: 'Onboarding',
          url: '/onboarding',
        },
      ];
    } else {
      children = [
        {
          label: 'Onboarding',
          children: [
            {
              label: 'Smart',
              url: '/onboarding',
            },
            // {
            //   label: 'Super Smart',
            //   url: '/onboarding/super-smart',
            // },
          ],
        },
      ];
    }

    const configuradorChildren: MenuChild[] = [];

    if (shouldAddMenuProvedores) {
      configuradorChildren.push({
        label: this.labelService.get('provedores'),
        url: '/provedores',
      });
    }

    if (shouldAddMenuEmpresa) {
      configuradorChildren.push({
        label: 'Estrutura da Empresa',
        children: [
          {
            label: 'Cargos',
            url: '/estrutura-organizacional/cargo',
          },
          {
            label: 'Departamentos',
            url: '/estrutura-organizacional/departamento',
          },
          {
            label: 'Centros de Custos',
            url: '/estrutura-organizacional/centro-de-custo',
          },
        ],
      });
    }

    if (shouldAddMenuContratos) {
      configuradorChildren.push({
        label: 'Contratos',
        url: '/contratos',
      });
    }

    const childConfiguradorRegras: MenuChild[] = [
      {
        label: 'Configurador de Regras',
        children: configuradorChildren,
      },
    ];

    children.push(...childConfiguradorRegras);

    // Leitor de e-mail da empresa selecionada: enderecos da empresa (so a Izii
    // altera), quem pode mandar fatura e quem recebe o resultado. Saiu de
    // Faturas para Cadastro em 23/10/2026, mesma rota. Visivel para todos: o
    // backend confere o vinculo e o tipo do usuario.
    children.push({
      label: 'Leitor de e-mail',
      url: '/remetentes-leitor-email',
    });

    // Logins que os robos de fatura usam nos portais das operadoras. So IziiAdmin
    // salva (o backend recusa os outros), entao o item so aparece para ele.
    if (this.usuario?.tipo === TipoUsuario.IziiAdmin) {
      children.push({
        label: 'Logins dos RPAs',
        url: '/credenciais-rpa',
      });
    }

    return {
      label: 'Cadastro',
      faIcon: faUserPen,
      children,
    };
  }

  private getMenuSair(): MenuItem {
    return {
      label: 'Sair',
      faIcon: faArrowRightFromBracket,
      onClick: () => {
        const loaderRef = this.loader.open('Saindo...');
        this.authService
          .signOut()
          .subscribe(() => {
            this.router.navigate(['autenticacao', 'entrar']);
          })
          .add(() => loaderRef.close());
      },
    };
  }

  private getMenuAtendimento(): MenuItem {
    return {
      label: 'Painel de Atendimento',
      faIcon: faHeadset,
      url: '/painel-de-atendimento',
    };
  }

  private getMenuTarefas(): MenuItem {
    return {
      label: 'Painel de Tarefas',
      faIcon: faListCheck,
      url: '/painel-de-tarefas',
    };
  }

  private getMenuBatimentoDeFatura(): MenuItem {
    return {
      label: 'Faturas',
      faIcon: faFileInvoiceDollar,
      children: [
        {
          label: 'Dashboard de Faturas',
          url: '/dashboard-faturas',
        },
        {
          label: 'Conciliação de Fatura',
          url: '/conferencia-de-fatura',
        },
        {
          label: 'Boletos e Notas Fiscais',
          url: '/conferencia-de-fatura/boletos',
        },
        {
          label: 'Fila Macro (resolver em lote)',
          url: '/conferencia-de-fatura/fila-macro',
        },
        {
          label: 'Cadastro de Rúbricas',
          url: '/rubricas-conciliacao-fatura',
        },
        {
          label: 'Extração de Críticas',
          url: '/extracao-criticas',
        },
      ],
    };
  }

  private getMenuMaisBeneficios(): MenuItem {
    return {
      label: 'Mais Benefícios',
      faIcon: faClipboardList,
      url: '/mais-beneficios',
    };
  }

  private getMenuManual(): MenuItem {
    return {
      label: 'Baixar Manual do Hub',
      faIcon: faBook,
      onClick: () => {
        downloadFileByUrl('../../../../assets/manuais/manual-hub.docx');
      },
    };
  }

  private getMenuAreaAdministrativa(): MenuItem {
    return {
      label: 'Área Administrativa',
      faIcon: faUsersCog,
      children: [
        {
          label: 'Companhias',
          url: 'area-administrativa/companhias',
        },
        {
          label: 'Usuários',
          url: 'area-administrativa/usuarios',
        },
        {
          label: 'Registro de Atividades',
          url: 'area-administrativa/registro-de-atividades',
        },
        {
          label: 'Gestão dos Portais',
          url: 'gestao-dos-portais',
        },
      ],
    };
  }

  private getMenuAreaBeneficiario(): MenuItem {
    const children: MenuChild[] = [
      {
        label: 'Beneficiários',
        url: '/beneficiarios',
      },
      {
        label: 'Importações de Movimentação',
        url: '/mov-cad',
      },
      {
        label: this.labelService.get('exportacoesParaProvedor'),
        url: '/beneficiarios/exportacoes',
      },
    ];

    // Adiciona o menu de Integrações se o usuário tiver permissão
    if (this.temPermissao('Integrações')) {
      children.push({
        label: this.labelService.get('integracoesComProvedores'),
        children: [
          // {
          //   label: 'Integrações Manuais',
          //   url: '/mov-cad-manual',
          // },
          {
            label: 'Integrações',
            url: '/integracoes',
          },
          {
            label: 'Logs de integração',
            url: '/integracoes/logs',
          },
          {
            label: 'Conferencia de Importação',
            onClick: () => this.abrirModalConferenciaImportacao(),
          },
        ],
      });
    }

    return {
      label: 'Área do Beneficiário',
      faIcon: faUser,
      children,
    };
  }

  private shouldShowAdminAreaMenu(): boolean {
    return [TipoUsuario.IziiAdmin, TipoUsuario.Admin].some(
      (t) => t === this.usuario?.tipo
    );
  }

  private shouldShowAreaBeneficiarioMenu(): boolean {
    return this.temPermissao('Beneficiários') &&
           this.onboardingStatus >= OnboardingStatus.Contrato;
  }

  private temPermissaoFactory(): (funcionalidade: string) => boolean {
    return (funcionalidade) =>
      !!(
        this.usuario.respostaPermissoesDTO.find(
          (p) => p.funcionalidade === funcionalidade
        )?.ehPermitido ?? false
      );
  }

  private abrirModalConferenciaImportacao(): void {
    this.matDialog.open(ModalConferenciaImportacaoComponent, {
      width: '700px',
    });
  }

  GetAppName() {
    return 'Benefits Conector';
  }
}
